/**
 * Timezone utilities for handling UTC backend data and displaying in PST.
 *
 * Architecture:
 * - Backend sends all timestamps in UTC (ISO 8601 format with 'Z')
 * - Frontend parses UTC and converts to APP_TIMEZONE for display
 * - Transaction dates are date-only strings (no time component)
 */

import { format, parseISO } from "date-fns";
import { formatInTimeZone, toZonedTime } from "date-fns-tz";

// Application timezone - change this to adjust display timezone for entire app
export const APP_TIMEZONE = "America/Los_Angeles";

/**
 * Parse a UTC timestamp string from the backend.
 *
 * @param {string} utcString - ISO 8601 UTC timestamp (e.g., "2025-10-26T13:30:00Z")
 * @returns {Date} JavaScript Date object
 */
export const parseUTCTimestamp = (utcString) => {
  if (!utcString) return null;
  return parseISO(utcString);
};

/**
 * Parse a date-only string (YYYY-MM-DD) as a local date.
 * Used for transaction dates which represent a calendar day, not a moment in time.
 *
 * @param {string} dateString - ISO date string (e.g., "2025-10-26")
 * @returns {Date} JavaScript Date object at midnight local time
 */
export const parseDateOnly = (dateString) => {
  if (!dateString) return null;
  const dateOnly = dateString.split("T")[0]; // Handle both "2025-10-26" and "2025-10-26T00:00:00Z"
  const [year, month, day] = dateOnly.split("-").map(Number);
  return new Date(year, month - 1, day); // Month is 0-indexed
};

/**
 * Format a UTC timestamp for display in APP_TIMEZONE.
 *
 * @param {string} utcString - ISO 8601 UTC timestamp
 * @param {string} formatString - date-fns format string (e.g., "MMM d, yyyy h:mm a")
 * @returns {string} Formatted date string in APP_TIMEZONE
 */
export const formatTimestamp = (utcString, formatString = "MMM d, yyyy h:mm a") => {
  if (!utcString) return "";
  const date = parseUTCTimestamp(utcString);
  return formatInTimeZone(date, APP_TIMEZONE, formatString);
};

/**
 * Format a date-only string for display.
 *
 * @param {string} dateString - ISO date string (e.g., "2025-10-26")
 * @param {string} formatString - date-fns format string (e.g., "MMM d, yyyy")
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, formatString = "MMM d, yyyy") => {
  if (!dateString) return "";
  const date = parseDateOnly(dateString);
  return format(date, formatString);
};

/**
 * Format a UTC timestamp as "time ago" (e.g., "2 hours ago", "3 days ago").
 *
 * @param {string} utcString - ISO 8601 UTC timestamp
 * @returns {string} Human-readable time ago string
 */
export const formatTimeAgo = (utcString) => {
  if (!utcString) return "";

  const date = parseUTCTimestamp(utcString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return "just now";
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }
  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }
  const days = Math.floor(seconds / 86400);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
  }
  if (days < 365) {
    const months = Math.floor(days / 30);
    return `${months} month${months === 1 ? "" : "s"} ago`;
  }
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? "" : "s"} ago`;
};

/**
 * Get the current date/time in APP_TIMEZONE.
 *
 * @returns {Date} Current date/time converted to APP_TIMEZONE
 */
export const getCurrentTimeInAppTimezone = () => {
  return toZonedTime(new Date(), APP_TIMEZONE);
};

/**
 * Format a date range for display.
 * Handles single-day periods, multi-day periods, and year boundaries.
 *
 * @param {string} startUTC - ISO 8601 UTC timestamp for start
 * @param {string} endUTC - ISO 8601 UTC timestamp for end
 * @returns {string} Formatted date range (empty for single-day periods)
 */
export const formatDateRange = (startUTC, endUTC) => {
  if (!startUTC || !endUTC) return "";

  const startDate = parseUTCTimestamp(startUTC);
  const endDate = parseUTCTimestamp(endUTC);

  // Convert to APP_TIMEZONE for comparison
  const startInTZ = toZonedTime(startDate, APP_TIMEZONE);
  const endInTZ = toZonedTime(endDate, APP_TIMEZONE);

  // Check if same calendar day in APP_TIMEZONE
  const startDateStr = format(startInTZ, "yyyy-MM-dd");
  const endDateStr = format(endInTZ, "yyyy-MM-dd");

  if (startDateStr === endDateStr) {
    // Single day period - don't show date range
    return "";
  }

  // Multi-day period - show range
  const currentYear = new Date().getFullYear();
  const startYear = startInTZ.getFullYear();
  const endYear = endInTZ.getFullYear();

  // Show year if not current year
  const shouldShowYear = startYear !== currentYear || endYear !== currentYear;

  const formatStr = shouldShowYear ? "M/d/yyyy" : "M/d";

  const startFormatted = format(startInTZ, formatStr);
  const endFormatted = format(endInTZ, formatStr);

  return `${startFormatted} - ${endFormatted}`;
};
